import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { MetricsCards } from '@/components/dashboard/MetricsCards';
import { ClientsTable } from '@/components/dashboard/ClientsTable';
import { ChatModal } from '@/components/dashboard/ChatModal';
import { ProposalControlsPanel } from '@/components/admin/ProposalControlsPanel';
import { archiveConversation, deleteConversation, setConversationActive } from '@/lib/orcaBackend';

interface ConversationMessage {
  role: string;
  content: string;
  datetime?: string;
}

interface Conversation {
  id: string;
  client_id: string;
  budget_eur: number;
  start_date: string;
  end_date: string;
  destination: string;
  summary: string;
  transcript: ConversationMessage[];
  closing_probability: number;
  last_updated: string;
  broker_notes: string;
  status: string;
  active: boolean;
  num_guests: number;
  broker_id: string | null;
  clients?: {
    full_name: string;
    email: string;
    phone: string;
    preferred_budget_eur: number;
    taste_notes: string;
  };
}

const Dashboard = () => {
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [orderBy, setOrderBy] = useState<'all' | 'active' | 'paused' | 'archived'>('all');
  const { user, session, loading: authLoading, profile } = useAuth();

  // Fetch ALL conversations (unfiltered) for metrics
  const { data: allConversations = [] } = useQuery({
    queryKey: ['all-conversations', profile?.brokerage_id, profile?.broker_id],
    queryFn: async () => {
      console.log('Fetching all conversations for metrics with profile:', profile);

      const base = supabase
        .from('conversations')
        .select(`
          *,
          clients (
            full_name,
            email,
            phone,
            preferred_budget_eur,
            taste_notes
          )
        `);

      // Filter conversations: show only unassigned OR assigned to the current broker
      const brokerageId = profile?.brokerage_id;
      const brokerId = profile?.broker_id;
      
      let query = base.eq('brokerage_id', brokerageId);
      
      // If user is a broker, show only unassigned conversations OR conversations assigned to them
      if (brokerId) {
        query = query.or(`broker_id.is.null,broker_id.eq.${brokerId}`);
      } else {
        // If user is not a broker (e.g., brokerage admin), show all conversations in their brokerage
        // This maintains existing behavior for admin users
      }

      const { data, error } = await query.order('last_updated', { ascending: false });
      
      if (error) {
        console.error('Error fetching all conversations:', error);
        throw error;
      }
      
      // ... keep existing code (transcript parsing and mapping)
      return (data || [])
        .filter(item => (item.status || 'active') !== 'archived')
        .map(item => {
        let parsedTranscript: ConversationMessage[] = [];
        
        if (item.transcript) {
          try {
            if (Array.isArray(item.transcript)) {
              parsedTranscript = item.transcript.map((msg: { role?: string; content?: string; datetime?: string }) => ({
                role: msg.role || '',
                content: msg.content || '',
                datetime: msg.datetime
              }));
            } else if (typeof item.transcript === 'string') {
              const parsed = JSON.parse(item.transcript);
              if (Array.isArray(parsed)) {
                parsedTranscript = parsed.map((msg: { role?: string; content?: string; datetime?: string }) => ({
                  role: msg.role || '',
                  content: msg.content || '',
                  datetime: msg.datetime
                }));
              }
            }
          } catch (e) {
            console.error('Error parsing transcript for conversation', item.id, ':', e);
            parsedTranscript = [];
          }
        }
        
        return {
          id: item.id,
          client_id: item.client_id,
          budget_eur: item.budget_eur,
          start_date: item.start_date,
          end_date: item.end_date,
          destination: item.destination,
          summary: item.summary,
          transcript: parsedTranscript,
          closing_probability: item.closing_probability,
          last_updated: item.last_updated,
          broker_notes: item.broker_notes,
          status: item.status || 'active',
          active: item.active,
          num_guests: item.num_guests,
          broker_id: item.broker_id,
          clients: item.clients
        } as Conversation;
      });
    },
    enabled: !!user && !!profile,
    refetchInterval: 1000 // Refresh every 1 second
  });

  // Fetch filtered conversations for the table
  const { data: conversations = [], isLoading, refetch } = useQuery({
    queryKey: ['conversations', profile?.brokerage_id, profile?.broker_id, orderBy],
    queryFn: async () => {
      console.log('Fetching filtered conversations with profile:', profile);

      const base = supabase
        .from('conversations')
        .select(`
          *,
          clients (
            full_name,
            email,
            phone,
            preferred_budget_eur,
            taste_notes
          )
        `);

      // Filter conversations: show only unassigned OR assigned to the current broker
      const brokerageId = profile?.brokerage_id;
      const brokerId = profile?.broker_id;
      
      let query = base.eq('brokerage_id', brokerageId);
      
      // If user is a broker, show only unassigned conversations OR conversations assigned to them
      if (brokerId) {
        query = query.or(`broker_id.is.null,broker_id.eq.${brokerId}`);
      } else {
        // If user is not a broker (e.g., brokerage admin), show all conversations in their brokerage
        // This maintains existing behavior for admin users
      }

      // Add ordering filter based on active/archive status
      if (orderBy === 'archived') {
        query = query.eq('status', 'archived');
      }

      if (orderBy === 'active') {
        query = query.eq('active', true);
      } else if (orderBy === 'paused') {
        query = query.eq('active', false);
      }

      const { data, error } = await query.order('last_updated', { ascending: false });
      
      if (error) {
        console.error('Error fetching conversations:', error);
        throw error;
      }
      
      // ... keep existing code (transcript parsing and mapping)
      return (data || [])
        .filter(item => orderBy === 'archived' || (item.status || 'active') !== 'archived')
        .map(item => {
        let parsedTranscript: ConversationMessage[] = [];
        
        if (item.transcript) {
          try {
            if (Array.isArray(item.transcript)) {
              parsedTranscript = item.transcript.map((msg: { role?: string; content?: string; datetime?: string }) => ({
                role: msg.role || '',
                content: msg.content || '',
                datetime: msg.datetime
              }));
            } else if (typeof item.transcript === 'string') {
              const parsed = JSON.parse(item.transcript);
              if (Array.isArray(parsed)) {
                parsedTranscript = parsed.map((msg: { role?: string; content?: string; datetime?: string }) => ({
                  role: msg.role || '',
                  content: msg.content || '',
                  datetime: msg.datetime
                }));
              }
            }
          } catch (e) {
            console.error('Error parsing transcript for conversation', item.id, ':', e);
            parsedTranscript = [];
          }
        }
        
        return {
          id: item.id,
          client_id: item.client_id,
          budget_eur: item.budget_eur,
          start_date: item.start_date,
          end_date: item.end_date,
          destination: item.destination,
          summary: item.summary,
          transcript: parsedTranscript,
          closing_probability: item.closing_probability,
          last_updated: item.last_updated,
          broker_notes: item.broker_notes,
          status: item.status || 'active',
          active: item.active,
          num_guests: item.num_guests,
          broker_id: item.broker_id,
          clients: item.clients
        } as Conversation;
      });
    },
    enabled: !!user && !!profile,
    refetchInterval: 1000 // Refresh every 1 second
  });

  // Remove duplicates by client_id and sort by most recent last_updated
  const uniqueConversations = React.useMemo(() => {
    const clientMap = new Map<string, Conversation>();
    
    conversations.forEach(conv => {
      const existingConv = clientMap.get(conv.client_id);
      if (!existingConv || new Date(conv.last_updated) > new Date(existingConv.last_updated)) {
        clientMap.set(conv.client_id, conv);
      }
    });
    
    return Array.from(clientMap.values()).sort((a, b) => {
      const dateA = new Date(a.last_updated || 0);
      const dateB = new Date(b.last_updated || 0);
      return dateB.getTime() - dateA.getTime();
    });
  }, [conversations]);

  // Remove duplicates for all conversations (for metrics) - UNCHANGED by filtering
  const uniqueAllConversations = React.useMemo(() => {
    const clientMap = new Map<string, Conversation>();
    
    allConversations.forEach(conv => {
      const existingConv = clientMap.get(conv.client_id);
      if (!existingConv || new Date(conv.last_updated) > new Date(existingConv.last_updated)) {
        clientMap.set(conv.client_id, conv);
      }
    });
    
    return Array.from(clientMap.values());
  }, [allConversations]);

  const handleViewChat = (conversation: Conversation) => {
    console.log('Opening chat modal for conversation:', conversation.id);
    console.log('Transcript data:', conversation.transcript);
    setSelectedConversation(conversation);
  };

  const handleRefresh = () => {
    console.log('Refreshing conversations data...');
    refetch();
  };

  const handleConversationUpdated = (updatedConversation: Conversation) => {
    setSelectedConversation((currentConversation) =>
      currentConversation?.id === updatedConversation.id
        ? { ...currentConversation, ...updatedConversation }
        : currentConversation
    );
    refetch();
  };

  const handleToggleActive = async (conversationId: string, active: boolean) => {
    try {
      const updatedConversation = await setConversationActive({
        conversationId,
        active,
        accessToken: session?.access_token,
        fallbackBackendUrl: profile?.ngrok_url,
      });

      setSelectedConversation((currentConversation) =>
        currentConversation?.id === updatedConversation.id
          ? { ...currentConversation, ...updatedConversation }
          : currentConversation
      );

      // Refresh the data after updating
      refetch();
    } catch (error) {
      console.error('Failed to toggle conversation status:', error);
      alert(
        error instanceof Error
          ? error.message
          : 'Failed to update conversation status.'
      );
    }
  };

  const handleArchiveConversation = async (conversationId: string) => {
    try {
      await archiveConversation({
        conversationId,
        accessToken: session?.access_token,
        fallbackBackendUrl: profile?.ngrok_url,
      });

      setSelectedConversation((currentConversation) =>
        currentConversation?.id === conversationId ? null : currentConversation
      );
      refetch();
    } catch (error) {
      console.error('Failed to archive conversation:', error);
      alert(
        error instanceof Error
          ? error.message
          : 'Failed to archive conversation.'
      );
    }
  };

  const handleDeleteConversation = async (conversationId: string) => {
    try {
      await deleteConversation({
        conversationId,
        accessToken: session?.access_token,
        fallbackBackendUrl: profile?.ngrok_url,
      });

      setSelectedConversation((currentConversation) =>
        currentConversation?.id === conversationId ? null : currentConversation
      );
      refetch();
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      alert(
        error instanceof Error
          ? error.message
          : 'Failed to delete conversation.'
      );
    }
  };

  // If auth is still loading, show loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-gray-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-gray-50 to-slate-100">
      <DashboardHeader />
      
      <div className="container mx-auto px-4 py-5 sm:px-6 sm:py-8">
        <MetricsCards conversations={uniqueAllConversations} />

        <ProposalControlsPanel
          brokerageId={profile?.brokerage_id || ''}
          conversations={uniqueConversations}
        />
        
        <div className="mt-8">
          <ClientsTable 
            conversations={uniqueConversations} 
            isLoading={isLoading}
            onViewChat={handleViewChat}
            onRefresh={handleRefresh}
            onToggleActive={handleToggleActive}
            onArchiveConversation={handleArchiveConversation}
            onDeleteConversation={handleDeleteConversation}
            orderBy={orderBy}
            onOrderByChange={setOrderBy}
          />
        </div>
      </div>

      {selectedConversation && (
        <ChatModal 
          conversation={selectedConversation} 
          onClose={() => setSelectedConversation(null)} 
          onConversationUpdated={handleConversationUpdated}
        />
      )}
    </div>
  );
};

export default Dashboard;
